<script setup>
import { ref, computed, reactive, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { required, helpers, url } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useToggle } from '@vueuse/core';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import AccessToken from 'dashboard/routes/dashboard/settings/profile/AccessToken.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  selectedBot: {
    type: Object,
    default: () => ({}),
  },
});

const MODAL_TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
};

const BOT_TYPES = {
  WEBHOOK: 'webhook',
  TYPEBOT: 'typebot',
  FOCOSIA: 'focosia',
};

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const uiFlags = useMapGetter('agentBots/getUIFlags');

const formState = reactive({
  botType: BOT_TYPES.WEBHOOK,
  botName: '',
  botDescription: '',
  botUrl: '',
  botAvatar: null,
  botAvatarUrl: '',
  typebotApiUrl: '',
  typebotPublicName: '',
  typebotApiToken: '',
  typebotTriggerType: 'all',
  typebotTriggerOperator: 'contains',
  typebotTriggerValue: '',
  typebotExpireInMinutes: '',
  typebotDefaultDelayMessage: '',
  typebotDebounceTime: '',
  typebotFinishKeyword: '',
  typebotIgnoreGroups: false,
  typebotIgnoredTargets: [],
  focosiaAgentUrl: '',
  focosiaApiKey: '',
  focosiaAgentId: '',
  focosiaTriggerType: 'all',
  focosiaTriggerOperator: 'contains',
  focosiaTriggerValue: '',
  focosiaExpireInMinutes: '',
  focosiaDefaultDelayMessage: '',
  focosiaDebounceTime: '',
  focosiaFinishKeyword: '',
  focosiaIgnoreGroups: false,
  focosiaIgnoredTargets: [],
});

const [showAccessToken, toggleAccessToken] = useToggle();
const accessToken = ref('');

const numericValidator = helpers.withMessage(
  () => t('AGENT_BOTS.FORM.ERRORS.TYPEBOT.NUMERIC'),
  value => !value || /^\d+$/.test(value)
);

const validationRules = computed(() => {
  const rules = {
    botName: {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.NAME'),
        required
      ),
    },
  };

  if (formState.botType === BOT_TYPES.WEBHOOK) {
    rules.botUrl = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.URL'),
        required
      ),
      url: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.VALID_URL'),
        url
      ),
    };
  }

  if (formState.botType === BOT_TYPES.TYPEBOT) {
    rules.typebotApiUrl = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.TYPEBOT.API_URL'),
        required
      ),
      url: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.TYPEBOT.API_URL_INVALID'),
        url
      ),
    };
    rules.typebotPublicName = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.TYPEBOT.PUBLIC_NAME'),
        required
      ),
    };
    rules.typebotTriggerType = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.TYPEBOT.TRIGGER_TYPE'),
        required
      ),
    };

    if (formState.typebotTriggerType === 'keyword') {
      rules.typebotTriggerOperator = {
        required: helpers.withMessage(
          () => t('AGENT_BOTS.FORM.ERRORS.TYPEBOT.TRIGGER_OPERATOR'),
          required
        ),
      };
      rules.typebotTriggerValue = {
        required: helpers.withMessage(
          () => t('AGENT_BOTS.FORM.ERRORS.TYPEBOT.TRIGGER_VALUE'),
          required
        ),
      };
    }

    rules.typebotExpireInMinutes = { numericValidator };
    rules.typebotDefaultDelayMessage = { numericValidator };
    rules.typebotDebounceTime = { numericValidator };
  }

  if (formState.botType === BOT_TYPES.FOCOSIA) {
    rules.focosiaAgentUrl = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.FOCOSIA.AGENT_URL'),
        required
      ),
      validUrl: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.FOCOSIA.AGENT_URL_INVALID'),
        value => !value || /^https?:\/\/.+/.test(value)
      ),
    };
    rules.focosiaApiKey = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.FOCOSIA.API_KEY'),
        required
      ),
    };
    rules.focosiaAgentId = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.FOCOSIA.AGENT_ID'),
        required
      ),
    };
    rules.focosiaTriggerType = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.FOCOSIA.TRIGGER_TYPE'),
        required
      ),
    };
    if (formState.focosiaTriggerType === 'keyword') {
      rules.focosiaTriggerOperator = {
        required: helpers.withMessage(
          () => t('AGENT_BOTS.FORM.ERRORS.FOCOSIA.TRIGGER_OPERATOR'),
          required
        ),
      };
      rules.focosiaTriggerValue = {
        required: helpers.withMessage(
          () => t('AGENT_BOTS.FORM.ERRORS.FOCOSIA.TRIGGER_VALUE'),
          required
        ),
      };
    }
    rules.focosiaExpireInMinutes = { numericValidator };
    rules.focosiaDefaultDelayMessage = { numericValidator };
    rules.focosiaDebounceTime = { numericValidator };
  }

  return rules;
});

const v$ = useVuelidate(validationRules, formState);

const botTypeOptions = computed(() => [
  {
    label: t('AGENT_BOTS.FORM.BOT_TYPE.WEBHOOK'),
    value: BOT_TYPES.WEBHOOK,
  },
  {
    label: t('AGENT_BOTS.FORM.BOT_TYPE.TYPEBOT'),
    value: BOT_TYPES.TYPEBOT,
  },
  {
    label: t('AGENT_BOTS.FORM.BOT_TYPE.FOCOSIA'),
    value: BOT_TYPES.FOCOSIA,
  },
]);

const selectedBotTypeLabel = computed(() => {
  const option = botTypeOptions.value.find(
    currentOption => currentOption.value === formState.botType
  );
  return option ? option.label : '';
});

const triggerTypeOptions = computed(() => [
  {
    label: t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_TYPE.OPTIONS.ALL'),
    value: 'all',
  },
  {
    label: t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_TYPE.OPTIONS.KEYWORD'),
    value: 'keyword',
  },
]);

const triggerOperatorOptions = computed(() => [
  {
    label: t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_OPERATOR.OPTIONS.CONTAINS'),
    value: 'contains',
  },
  {
    label: t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_OPERATOR.OPTIONS.EQUALS'),
    value: 'equals',
  },
  {
    label: t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_OPERATOR.OPTIONS.STARTS_WITH'),
    value: 'starts_with',
  },
  {
    label: t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_OPERATOR.OPTIONS.ENDS_WITH'),
    value: 'ends_with',
  },
  {
    label: t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_OPERATOR.OPTIONS.REGEX'),
    value: 'regex',
  },
]);

const selectedTriggerTypeLabel = computed(() => {
  const currentTriggerType =
    formState.botType === BOT_TYPES.FOCOSIA
      ? formState.focosiaTriggerType
      : formState.typebotTriggerType;
  const option = triggerTypeOptions.value.find(
    currentOption => currentOption.value === currentTriggerType
  );
  return option ? option.label : '';
});

const selectedTriggerOperatorLabel = computed(() => {
  const currentTriggerOperator =
    formState.botType === BOT_TYPES.FOCOSIA
      ? formState.focosiaTriggerOperator
      : formState.typebotTriggerOperator;
  const option = triggerOperatorOptions.value.find(
    currentOption => currentOption.value === currentTriggerOperator
  );
  return option ? option.label : '';
});

const isLoading = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? uiFlags.value.isCreating
    : uiFlags.value.isUpdating
);

const dialogTitle = computed(() => {
  if (showAccessToken.value) {
    return t('AGENT_BOTS.ACCESS_TOKEN.TITLE');
  }

  return props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.ADD.TITLE')
    : t('AGENT_BOTS.EDIT.TITLE');
});

const dialogDescription = computed(() => {
  if (showAccessToken.value) {
    return t('AGENT_BOTS.ACCESS_TOKEN.DESCRIPTION');
  }
  return '';
});

const confirmButtonLabel = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.FORM.CREATE')
    : t('AGENT_BOTS.FORM.UPDATE')
);

const botNameError = computed(() =>
  v$.value.botName.$error ? v$.value.botName.$errors[0]?.$message : ''
);

const botUrlError = computed(() =>
  v$.value.botUrl.$error ? v$.value.botUrl.$errors[0]?.$message : ''
);

const typebotApiUrlError = computed(() =>
  v$.value.typebotApiUrl?.$error
    ? v$.value.typebotApiUrl.$errors[0]?.$message
    : ''
);

const typebotPublicNameError = computed(() =>
  v$.value.typebotPublicName?.$error
    ? v$.value.typebotPublicName.$errors[0]?.$message
    : ''
);

const typebotTriggerTypeError = computed(() =>
  v$.value.typebotTriggerType?.$error
    ? v$.value.typebotTriggerType.$errors[0]?.$message
    : ''
);

const typebotTriggerOperatorError = computed(() =>
  v$.value.typebotTriggerOperator?.$error
    ? v$.value.typebotTriggerOperator.$errors[0]?.$message
    : ''
);

const typebotTriggerValueError = computed(() =>
  v$.value.typebotTriggerValue?.$error
    ? v$.value.typebotTriggerValue.$errors[0]?.$message
    : ''
);

const typebotExpireInMinutesError = computed(() =>
  v$.value.typebotExpireInMinutes?.$error
    ? v$.value.typebotExpireInMinutes.$errors[0]?.$message
    : ''
);

const typebotDefaultDelayMessageError = computed(() =>
  v$.value.typebotDefaultDelayMessage?.$error
    ? v$.value.typebotDefaultDelayMessage.$errors[0]?.$message
    : ''
);

const typebotDebounceTimeError = computed(() =>
  v$.value.typebotDebounceTime?.$error
    ? v$.value.typebotDebounceTime.$errors[0]?.$message
    : ''
);

const focosiaAgentUrlError = computed(() =>
  v$.value.focosiaAgentUrl?.$error
    ? v$.value.focosiaAgentUrl.$errors[0]?.$message
    : ''
);

const focosiaApiKeyError = computed(() =>
  v$.value.focosiaApiKey?.$error
    ? v$.value.focosiaApiKey.$errors[0]?.$message
    : ''
);

const focosiaAgentIdError = computed(() =>
  v$.value.focosiaAgentId?.$error
    ? v$.value.focosiaAgentId.$errors[0]?.$message
    : ''
);

const focosiaTriggerTypeError = computed(() =>
  v$.value.focosiaTriggerType?.$error
    ? v$.value.focosiaTriggerType.$errors[0]?.$message
    : ''
);

const focosiaTriggerOperatorError = computed(() =>
  v$.value.focosiaTriggerOperator?.$error
    ? v$.value.focosiaTriggerOperator.$errors[0]?.$message
    : ''
);

const focosiaTriggerValueError = computed(() =>
  v$.value.focosiaTriggerValue?.$error
    ? v$.value.focosiaTriggerValue.$errors[0]?.$message
    : ''
);

const focosiaExpireInMinutesError = computed(() =>
  v$.value.focosiaExpireInMinutes?.$error
    ? v$.value.focosiaExpireInMinutes.$errors[0]?.$message
    : ''
);

const focosiaDefaultDelayMessageError = computed(() =>
  v$.value.focosiaDefaultDelayMessage?.$error
    ? v$.value.focosiaDefaultDelayMessage.$errors[0]?.$message
    : ''
);

const focosiaDebounceTimeError = computed(() =>
  v$.value.focosiaDebounceTime?.$error
    ? v$.value.focosiaDebounceTime.$errors[0]?.$message
    : ''
);

const showAccessTokenInput = computed(
  () =>
    showAccessToken.value ||
    props.type === MODAL_TYPES.EDIT ||
    accessToken.value
);

const resetForm = () => {
  Object.assign(formState, {
    botType: BOT_TYPES.WEBHOOK,
    botName: '',
    botDescription: '',
    botUrl: '',
    botAvatar: null,
    botAvatarUrl: '',
    typebotApiUrl: '',
    typebotPublicName: '',
    typebotApiToken: '',
    typebotTriggerType: 'all',
    typebotTriggerOperator: 'contains',
    typebotTriggerValue: '',
    typebotExpireInMinutes: '',
    typebotDefaultDelayMessage: '',
    typebotDebounceTime: '',
    typebotFinishKeyword: '',
    typebotIgnoreGroups: false,
    typebotIgnoredTargets: [],
    focosiaAgentUrl: '',
    focosiaApiKey: '',
    focosiaAgentId: '',
    focosiaTriggerType: 'all',
    focosiaTriggerOperator: 'contains',
    focosiaTriggerValue: '',
    focosiaExpireInMinutes: '',
    focosiaDefaultDelayMessage: '',
    focosiaDebounceTime: '',
    focosiaFinishKeyword: '',
    focosiaIgnoreGroups: false,
    focosiaIgnoredTargets: [],
  });
  v$.value.$reset();
};

const handleImageUpload = ({ file, url: avatarUrl }) => {
  formState.botAvatar = file;
  formState.botAvatarUrl = avatarUrl;
};

const handleAvatarDelete = async () => {
  if (props.selectedBot?.id) {
    try {
      await store.dispatch(
        'agentBots/deleteAgentBotAvatar',
        props.selectedBot.id
      );
      formState.botAvatar = null;
      formState.botAvatarUrl = '';
      useAlert(t('AGENT_BOTS.AVATAR.SUCCESS_DELETE'));
    } catch (error) {
      useAlert(t('AGENT_BOTS.AVATAR.ERROR_DELETE'));
    }
  } else {
    formState.botAvatar = null;
    formState.botAvatarUrl = '';
  }
};

const handleSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  if (showAccessToken.value) return;

  const isWebhook = formState.botType === BOT_TYPES.WEBHOOK;
  const isTypebot = formState.botType === BOT_TYPES.TYPEBOT;
  const isFocosia = formState.botType === BOT_TYPES.FOCOSIA;
  const sanitizedIgnoredTargets = isTypebot
    ? formState.typebotIgnoredTargets
        .map(value => value?.toString().trim())
        .filter(value => value)
    : [];
  const sanitizedFocosiaIgnoredTargets = isFocosia
    ? formState.focosiaIgnoredTargets
        .map(value => value?.toString().trim())
        .filter(value => value)
    : [];

  let botConfig = {};
  if (isWebhook) {
    botConfig = { webhook_url: formState.botUrl };
  } else if (isTypebot) {
    botConfig = {
      api_url: formState.typebotApiUrl,
      public_name: formState.typebotPublicName,
      api_token: formState.typebotApiToken,
      trigger_type: formState.typebotTriggerType,
      trigger_operator:
        formState.typebotTriggerType === 'keyword'
          ? formState.typebotTriggerOperator
          : '',
      trigger_value:
        formState.typebotTriggerType === 'keyword'
          ? formState.typebotTriggerValue
          : '',
      expire_in_minutes: formState.typebotExpireInMinutes,
      default_delay_message: formState.typebotDefaultDelayMessage,
      debounce_time: formState.typebotDebounceTime,
      finish_keyword: formState.typebotFinishKeyword,
      ignore_groups: formState.typebotIgnoreGroups,
      ignored_targets: sanitizedIgnoredTargets,
    };
  } else if (isFocosia) {
    botConfig = {
      agent_url: formState.focosiaAgentUrl,
      api_key: formState.focosiaApiKey,
      agent_id: formState.focosiaAgentId,
      trigger_type: formState.focosiaTriggerType,
      trigger_operator:
        formState.focosiaTriggerType === 'keyword'
          ? formState.focosiaTriggerOperator
          : '',
      trigger_value:
        formState.focosiaTriggerType === 'keyword'
          ? formState.focosiaTriggerValue
          : '',
      expire_in_minutes: formState.focosiaExpireInMinutes,
      default_delay_message: formState.focosiaDefaultDelayMessage,
      debounce_time: formState.focosiaDebounceTime,
      finish_keyword: formState.focosiaFinishKeyword,
      ignore_groups: formState.focosiaIgnoreGroups,
      ignored_targets: sanitizedFocosiaIgnoredTargets,
    };
  }

  const botData = {
    name: formState.botName,
    description: formState.botDescription,
    outgoing_url: isWebhook ? formState.botUrl : '',
    bot_type: formState.botType,
    avatar: formState.botAvatar,
    bot_config: botConfig,
  };

  const isCreate = props.type === MODAL_TYPES.CREATE;

  try {
    const actionPayload = isCreate
      ? botData
      : { id: props.selectedBot.id, data: botData };

    const response = await store.dispatch(
      `agentBots/${isCreate ? 'create' : 'update'}`,
      actionPayload
    );

    const alertKey = isCreate
      ? t('AGENT_BOTS.ADD.API.SUCCESS_MESSAGE')
      : t('AGENT_BOTS.EDIT.API.SUCCESS_MESSAGE');
    useAlert(alertKey);

    // Show access token after creation
    if (isCreate) {
      const { access_token: responseAccessToken, id } = response || {};

      if (id && responseAccessToken) {
        accessToken.value = responseAccessToken;
        toggleAccessToken(true);
      } else {
        accessToken.value = '';
        dialogRef.value.close();
      }
    } else {
      dialogRef.value.close();
    }

    resetForm();
  } catch (error) {
    const errorKey = isCreate
      ? t('AGENT_BOTS.ADD.API.ERROR_MESSAGE')
      : t('AGENT_BOTS.EDIT.API.ERROR_MESSAGE');
    useAlert(errorKey);
  }
};

const initializeForm = () => {
  if (props.selectedBot && Object.keys(props.selectedBot).length) {
    const {
      name,
      description,
      outgoing_url: botUrl,
      thumbnail,
      bot_config: botConfig,
      access_token: botAccessToken,
      bot_type: botType,
    } = props.selectedBot;
    formState.botType = botType || BOT_TYPES.WEBHOOK;
    formState.botName = name || '';
    formState.botDescription = description || '';
    formState.botUrl = botUrl || botConfig?.webhook_url || '';
    formState.botAvatarUrl = thumbnail || '';

    if (formState.botType === BOT_TYPES.TYPEBOT) {
      formState.typebotApiUrl = botConfig?.api_url || '';
      formState.typebotPublicName = botConfig?.public_name || '';
      formState.typebotApiToken = botConfig?.api_token || '';
      formState.typebotTriggerType = botConfig?.trigger_type || 'all';
      formState.typebotTriggerOperator =
        botConfig?.trigger_operator || 'contains';
      formState.typebotTriggerValue = botConfig?.trigger_value || '';
      formState.typebotExpireInMinutes = botConfig?.expire_in_minutes || '';
      formState.typebotDefaultDelayMessage =
        botConfig?.default_delay_message || '';
      formState.typebotDebounceTime = botConfig?.debounce_time || '';
      formState.typebotFinishKeyword = botConfig?.finish_keyword || '';
      formState.typebotIgnoreGroups =
        typeof botConfig?.ignore_groups === 'string'
          ? ['true', '1', 'yes', 'on'].includes(
              botConfig.ignore_groups.toLowerCase()
            )
          : Boolean(botConfig?.ignore_groups);
      let existingIgnoredTargets = [];
      if (Array.isArray(botConfig?.ignored_targets)) {
        existingIgnoredTargets = botConfig.ignored_targets;
      } else if (botConfig?.ignored_targets) {
        existingIgnoredTargets = [botConfig.ignored_targets];
      }
      formState.typebotIgnoredTargets = existingIgnoredTargets
        .map(value => value?.toString().trim())
        .filter(value => value);
    } else if (formState.botType === BOT_TYPES.FOCOSIA) {
      formState.focosiaAgentUrl = botConfig?.agent_url || '';
      formState.focosiaApiKey = botConfig?.api_key || '';
      formState.focosiaAgentId = botConfig?.agent_id || '';
      formState.focosiaTriggerType = botConfig?.trigger_type || 'all';
      formState.focosiaTriggerOperator =
        botConfig?.trigger_operator || 'contains';
      formState.focosiaTriggerValue = botConfig?.trigger_value || '';
      formState.focosiaExpireInMinutes = botConfig?.expire_in_minutes || '';
      formState.focosiaDefaultDelayMessage =
        botConfig?.default_delay_message || '';
      formState.focosiaDebounceTime = botConfig?.debounce_time || '';
      formState.focosiaFinishKeyword = botConfig?.finish_keyword || '';
      formState.focosiaIgnoreGroups =
        typeof botConfig?.ignore_groups === 'string'
          ? ['true', '1', 'yes', 'on'].includes(
              botConfig.ignore_groups.toLowerCase()
            )
          : Boolean(botConfig?.ignore_groups);
      let existingFocosiaIgnoredTargets = [];
      if (Array.isArray(botConfig?.ignored_targets)) {
        existingFocosiaIgnoredTargets = botConfig.ignored_targets;
      } else if (botConfig?.ignored_targets) {
        existingFocosiaIgnoredTargets = [botConfig.ignored_targets];
      }
      formState.focosiaIgnoredTargets = existingFocosiaIgnoredTargets
        .map(value => value?.toString().trim())
        .filter(value => value);
    } else {
      formState.typebotApiUrl = '';
      formState.typebotPublicName = '';
      formState.typebotApiToken = '';
      formState.typebotTriggerType = 'all';
      formState.typebotTriggerOperator = 'contains';
      formState.typebotTriggerValue = '';
      formState.typebotExpireInMinutes = '';
      formState.typebotDefaultDelayMessage = '';
      formState.typebotDebounceTime = '';
      formState.typebotFinishKeyword = '';
      formState.typebotIgnoreGroups = false;
      formState.typebotIgnoredTargets = [];
      formState.focosiaAgentUrl = '';
      formState.focosiaApiKey = '';
      formState.focosiaAgentId = '';
      formState.focosiaTriggerType = 'all';
      formState.focosiaTriggerOperator = 'contains';
      formState.focosiaTriggerValue = '';
      formState.focosiaExpireInMinutes = '';
      formState.focosiaDefaultDelayMessage = '';
      formState.focosiaDebounceTime = '';
      formState.focosiaFinishKeyword = '';
      formState.focosiaIgnoreGroups = false;
      formState.focosiaIgnoredTargets = [];
    }

    if (botAccessToken && props.type === MODAL_TYPES.EDIT) {
      accessToken.value = botAccessToken;
    }
  } else {
    resetForm();
  }
};

const onCopyToken = async value => {
  await copyTextToClipboard(value);
  useAlert(t('AGENT_BOTS.ACCESS_TOKEN.COPY_SUCCESSFUL'));
};

const onResetToken = async () => {
  const response = await store.dispatch(
    'agentBots/resetAccessToken',
    props.selectedBot.id
  );
  if (response) {
    accessToken.value = response.access_token;
    useAlert(t('AGENT_BOTS.ACCESS_TOKEN.RESET_SUCCESS'));
  } else {
    useAlert(t('AGENT_BOTS.ACCESS_TOKEN.RESET_ERROR'));
  }
};

const closeModal = () => {
  if (!showAccessToken.value) v$.value?.$reset();
  accessToken.value = '';
  toggleAccessToken(false);
};

const onClickClose = () => {
  closeModal();
  dialogRef.value.close();
};

watch(() => props.selectedBot, initializeForm, { immediate: true, deep: true });

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="dialogTitle"
    :description="dialogDescription"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    @close="closeModal"
  >
    <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
      <div class="flex flex-col gap-4 max-h-[65vh] overflow-y-auto pr-2">
        <div
          v-if="!showAccessToken || type === MODAL_TYPES.EDIT"
          class="flex flex-col gap-4"
        >
          <div class="mb-2 flex flex-col items-start">
            <span class="mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('AGENT_BOTS.FORM.AVATAR.LABEL') }}
            </span>
            <Avatar
              :src="formState.botAvatarUrl"
              :name="formState.botName"
              :size="68"
              allow-upload
              icon-name="i-lucide-bot-message-square"
              @upload="handleImageUpload"
              @delete="handleAvatarDelete"
            />
          </div>

          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('AGENT_BOTS.FORM.BOT_TYPE.LABEL') }}
            </span>
            <SelectMenu
              :model-value="formState.botType"
              :options="botTypeOptions"
              :label="selectedBotTypeLabel"
              sub-menu-position="bottom"
              @update:model-value="value => (formState.botType = value)"
            />
          </div>

          <Input
            id="bot-name"
            v-model="formState.botName"
            :label="$t('AGENT_BOTS.FORM.NAME.LABEL')"
            :placeholder="$t('AGENT_BOTS.FORM.NAME.PLACEHOLDER')"
            :message="botNameError"
            :message-type="botNameError ? 'error' : 'info'"
            @blur="v$.botName.$touch()"
          />

          <TextArea
            id="bot-description"
            v-model="formState.botDescription"
            :label="$t('AGENT_BOTS.FORM.DESCRIPTION.LABEL')"
            :placeholder="$t('AGENT_BOTS.FORM.DESCRIPTION.PLACEHOLDER')"
          />

          <Input
            v-if="formState.botType === BOT_TYPES.WEBHOOK"
            id="bot-url"
            v-model="formState.botUrl"
            :label="$t('AGENT_BOTS.FORM.WEBHOOK_URL.LABEL')"
            :placeholder="$t('AGENT_BOTS.FORM.WEBHOOK_URL.PLACEHOLDER')"
            :message="botUrlError"
            :message-type="botUrlError ? 'error' : 'info'"
            @blur="v$.botUrl?.$touch()"
          />

          <div
            v-if="formState.botType === BOT_TYPES.TYPEBOT"
            class="flex flex-col gap-6"
          >
            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('AGENT_BOTS.FORM.TYPEBOT.SECTIONS.CONNECTION') }}
              </h3>
              <Input
                id="typebot-api-url"
                v-model="formState.typebotApiUrl"
                :label="$t('AGENT_BOTS.FORM.TYPEBOT.API_URL.LABEL')"
                :placeholder="$t('AGENT_BOTS.FORM.TYPEBOT.API_URL.PLACEHOLDER')"
                :message="typebotApiUrlError"
                :message-type="typebotApiUrlError ? 'error' : 'info'"
                @blur="v$.typebotApiUrl?.$touch()"
              />
              <Input
                id="typebot-public-name"
                v-model="formState.typebotPublicName"
                :label="$t('AGENT_BOTS.FORM.TYPEBOT.PUBLIC_NAME.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.TYPEBOT.PUBLIC_NAME.PLACEHOLDER')
                "
                :message="typebotPublicNameError"
                :message-type="typebotPublicNameError ? 'error' : 'info'"
                @blur="v$.typebotPublicName?.$touch()"
              />
              <Input
                id="typebot-api-token"
                v-model="formState.typebotApiToken"
                :label="$t('AGENT_BOTS.FORM.TYPEBOT.API_TOKEN.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.TYPEBOT.API_TOKEN.PLACEHOLDER')
                "
              />
            </div>

            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_SETTINGS.TITLE') }}
              </h3>
              <div class="flex flex-col gap-2">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_TYPE.LABEL') }}
                </span>
                <SelectMenu
                  :model-value="formState.typebotTriggerType"
                  :options="triggerTypeOptions"
                  :label="selectedTriggerTypeLabel"
                  sub-menu-position="bottom"
                  @update:model-value="
                    value => (formState.typebotTriggerType = value)
                  "
                />
                <p v-if="typebotTriggerTypeError" class="text-xs text-n-ruby-9">
                  {{ typebotTriggerTypeError }}
                </p>
              </div>

              <div
                v-if="formState.typebotTriggerType === 'keyword'"
                class="flex flex-col gap-3"
              >
                <div class="flex flex-col gap-2">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ $t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_OPERATOR.LABEL') }}
                  </span>
                  <SelectMenu
                    :model-value="formState.typebotTriggerOperator"
                    :options="triggerOperatorOptions"
                    :label="selectedTriggerOperatorLabel"
                    sub-menu-position="bottom"
                    @update:model-value="
                      value => (formState.typebotTriggerOperator = value)
                    "
                  />
                  <p
                    v-if="typebotTriggerOperatorError"
                    class="text-xs text-n-ruby-9"
                  >
                    {{ typebotTriggerOperatorError }}
                  </p>
                </div>
                <Input
                  id="typebot-trigger-value"
                  v-model="formState.typebotTriggerValue"
                  :label="$t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_VALUE.LABEL')"
                  :placeholder="
                    $t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_VALUE.PLACEHOLDER')
                  "
                  :message="typebotTriggerValueError"
                  :message-type="typebotTriggerValueError ? 'error' : 'info'"
                  @blur="v$.typebotTriggerValue?.$touch()"
                />
              </div>
            </div>

            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('AGENT_BOTS.FORM.TYPEBOT.GENERAL_SETTINGS.TITLE') }}
              </h3>
              <Input
                id="typebot-finish-keyword"
                v-model="formState.typebotFinishKeyword"
                :label="$t('AGENT_BOTS.FORM.TYPEBOT.FINISH_KEYWORD.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.TYPEBOT.FINISH_KEYWORD.PLACEHOLDER')
                "
                :message="
                  $t('AGENT_BOTS.FORM.TYPEBOT.FINISH_KEYWORD.HELP_TEXT')
                "
              />
              <Input
                id="typebot-expire"
                v-model="formState.typebotExpireInMinutes"
                :label="$t('AGENT_BOTS.FORM.TYPEBOT.EXPIRE_IN_MINUTES.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.TYPEBOT.EXPIRE_IN_MINUTES.PLACEHOLDER')
                "
                :message="typebotExpireInMinutesError"
                :message-type="typebotExpireInMinutesError ? 'error' : 'info'"
                @blur="v$.typebotExpireInMinutes?.$touch()"
              />
              <Input
                id="typebot-default-delay"
                v-model="formState.typebotDefaultDelayMessage"
                :label="
                  $t('AGENT_BOTS.FORM.TYPEBOT.DEFAULT_DELAY_MESSAGE.LABEL')
                "
                :placeholder="
                  $t(
                    'AGENT_BOTS.FORM.TYPEBOT.DEFAULT_DELAY_MESSAGE.PLACEHOLDER'
                  )
                "
                :message="typebotDefaultDelayMessageError"
                :message-type="
                  typebotDefaultDelayMessageError ? 'error' : 'info'
                "
                @blur="v$.typebotDefaultDelayMessage?.$touch()"
              />
              <Input
                id="typebot-debounce"
                v-model="formState.typebotDebounceTime"
                :label="$t('AGENT_BOTS.FORM.TYPEBOT.DEBOUNCE_TIME.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.TYPEBOT.DEBOUNCE_TIME.PLACEHOLDER')
                "
                :message="typebotDebounceTimeError"
                :message-type="typebotDebounceTimeError ? 'error' : 'info'"
                @blur="v$.typebotDebounceTime?.$touch()"
              />
            </div>

            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('AGENT_BOTS.FORM.TYPEBOT.IGNORED_CONTACTS.TITLE') }}
              </h3>
              <div
                class="flex items-start justify-between gap-3 rounded-lg border border-n-slate-6 bg-n-slate-1 px-3 py-3"
              >
                <div class="flex flex-col gap-1">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{
                      $t(
                        'AGENT_BOTS.FORM.TYPEBOT.IGNORED_CONTACTS.IGNORE_GROUPS_LABEL'
                      )
                    }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{
                      $t(
                        'AGENT_BOTS.FORM.TYPEBOT.IGNORED_CONTACTS.IGNORE_GROUPS_HELP_TEXT'
                      )
                    }}
                  </span>
                </div>
                <Switch
                  v-model="formState.typebotIgnoreGroups"
                  :aria-label="
                    $t(
                      'AGENT_BOTS.FORM.TYPEBOT.IGNORED_CONTACTS.IGNORE_GROUPS_LABEL'
                    )
                  "
                />
              </div>
              <div class="flex flex-col gap-2">
                <span class="text-sm font-medium text-n-slate-12">
                  {{
                    $t(
                      'AGENT_BOTS.FORM.TYPEBOT.IGNORED_CONTACTS.IDENTIFIERS_LABEL'
                    )
                  }}
                </span>
                <TagInput
                  v-model="formState.typebotIgnoredTargets"
                  allow-create
                  :placeholder="
                    $t(
                      'AGENT_BOTS.FORM.TYPEBOT.IGNORED_CONTACTS.IDENTIFIERS_PLACEHOLDER'
                    )
                  "
                />
                <p class="text-xs text-n-slate-11">
                  {{
                    $t(
                      'AGENT_BOTS.FORM.TYPEBOT.IGNORED_CONTACTS.IDENTIFIERS_HELP_TEXT'
                    )
                  }}
                </p>
              </div>
            </div>
          </div>

          <div
            v-if="formState.botType === BOT_TYPES.FOCOSIA"
            class="flex flex-col gap-6"
          >
            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('AGENT_BOTS.FORM.FOCOSIA.SECTIONS.CONNECTION') }}
              </h3>
              <Input
                id="focosia-agent-url"
                v-model="formState.focosiaAgentUrl"
                :label="$t('AGENT_BOTS.FORM.FOCOSIA.AGENT_URL.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.FOCOSIA.AGENT_URL.PLACEHOLDER')
                "
                :message="focosiaAgentUrlError"
                :message-type="focosiaAgentUrlError ? 'error' : 'info'"
                @blur="v$.focosiaAgentUrl?.$touch()"
              />
              <Input
                id="focosia-api-key"
                v-model="formState.focosiaApiKey"
                type="password"
                :label="$t('AGENT_BOTS.FORM.FOCOSIA.API_KEY.LABEL')"
                :placeholder="$t('AGENT_BOTS.FORM.FOCOSIA.API_KEY.PLACEHOLDER')"
                :message="focosiaApiKeyError"
                :message-type="focosiaApiKeyError ? 'error' : 'info'"
                @blur="v$.focosiaApiKey?.$touch()"
              />
              <Input
                id="focosia-agent-id"
                v-model="formState.focosiaAgentId"
                :label="$t('AGENT_BOTS.FORM.FOCOSIA.AGENT_ID.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.FOCOSIA.AGENT_ID.PLACEHOLDER')
                "
                :message="focosiaAgentIdError"
                :message-type="focosiaAgentIdError ? 'error' : 'info'"
                @blur="v$.focosiaAgentId?.$touch()"
              />
            </div>

            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('AGENT_BOTS.FORM.FOCOSIA.TRIGGER_SETTINGS.TITLE') }}
              </h3>
              <div class="flex flex-col gap-2">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('AGENT_BOTS.FORM.FOCOSIA.TRIGGER_TYPE.LABEL') }}
                </span>
                <SelectMenu
                  :model-value="formState.focosiaTriggerType"
                  :options="triggerTypeOptions"
                  :label="selectedTriggerTypeLabel"
                  sub-menu-position="bottom"
                  @update:model-value="
                    value => (formState.focosiaTriggerType = value)
                  "
                />
                <p v-if="focosiaTriggerTypeError" class="text-xs text-n-ruby-9">
                  {{ focosiaTriggerTypeError }}
                </p>
              </div>

              <div
                v-if="formState.focosiaTriggerType === 'keyword'"
                class="flex flex-col gap-3"
              >
                <div class="flex flex-col gap-2">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ $t('AGENT_BOTS.FORM.FOCOSIA.TRIGGER_OPERATOR.LABEL') }}
                  </span>
                  <SelectMenu
                    :model-value="formState.focosiaTriggerOperator"
                    :options="triggerOperatorOptions"
                    :label="selectedTriggerOperatorLabel"
                    sub-menu-position="bottom"
                    @update:model-value="
                      value => (formState.focosiaTriggerOperator = value)
                    "
                  />
                  <p
                    v-if="focosiaTriggerOperatorError"
                    class="text-xs text-n-ruby-9"
                  >
                    {{ focosiaTriggerOperatorError }}
                  </p>
                </div>
                <Input
                  id="focosia-trigger-value"
                  v-model="formState.focosiaTriggerValue"
                  :label="$t('AGENT_BOTS.FORM.FOCOSIA.TRIGGER_VALUE.LABEL')"
                  :placeholder="
                    $t('AGENT_BOTS.FORM.FOCOSIA.TRIGGER_VALUE.PLACEHOLDER')
                  "
                  :message="focosiaTriggerValueError"
                  :message-type="focosiaTriggerValueError ? 'error' : 'info'"
                  @blur="v$.focosiaTriggerValue?.$touch()"
                />
              </div>
            </div>

            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('AGENT_BOTS.FORM.FOCOSIA.GENERAL_SETTINGS.TITLE') }}
              </h3>
              <Input
                id="focosia-finish-keyword"
                v-model="formState.focosiaFinishKeyword"
                :label="$t('AGENT_BOTS.FORM.FOCOSIA.FINISH_KEYWORD.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.FOCOSIA.FINISH_KEYWORD.PLACEHOLDER')
                "
                :message="
                  $t('AGENT_BOTS.FORM.FOCOSIA.FINISH_KEYWORD.HELP_TEXT')
                "
              />
              <Input
                id="focosia-expire"
                v-model="formState.focosiaExpireInMinutes"
                :label="$t('AGENT_BOTS.FORM.FOCOSIA.EXPIRE_IN_MINUTES.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.FOCOSIA.EXPIRE_IN_MINUTES.PLACEHOLDER')
                "
                :message="focosiaExpireInMinutesError"
                :message-type="focosiaExpireInMinutesError ? 'error' : 'info'"
                @blur="v$.focosiaExpireInMinutes?.$touch()"
              />
              <Input
                id="focosia-default-delay"
                v-model="formState.focosiaDefaultDelayMessage"
                :label="
                  $t('AGENT_BOTS.FORM.FOCOSIA.DEFAULT_DELAY_MESSAGE.LABEL')
                "
                :placeholder="
                  $t(
                    'AGENT_BOTS.FORM.FOCOSIA.DEFAULT_DELAY_MESSAGE.PLACEHOLDER'
                  )
                "
                :message="focosiaDefaultDelayMessageError"
                :message-type="
                  focosiaDefaultDelayMessageError ? 'error' : 'info'
                "
                @blur="v$.focosiaDefaultDelayMessage?.$touch()"
              />
              <Input
                id="focosia-debounce"
                v-model="formState.focosiaDebounceTime"
                :label="$t('AGENT_BOTS.FORM.FOCOSIA.DEBOUNCE_TIME.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.FOCOSIA.DEBOUNCE_TIME.PLACEHOLDER')
                "
                :message="focosiaDebounceTimeError"
                :message-type="focosiaDebounceTimeError ? 'error' : 'info'"
                @blur="v$.focosiaDebounceTime?.$touch()"
              />
            </div>

            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('AGENT_BOTS.FORM.FOCOSIA.IGNORED_CONTACTS.TITLE') }}
              </h3>
              <div
                class="flex items-start justify-between gap-3 rounded-lg border border-n-slate-6 bg-n-slate-1 px-3 py-3"
              >
                <div class="flex flex-col gap-1">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{
                      $t(
                        'AGENT_BOTS.FORM.FOCOSIA.IGNORED_CONTACTS.IGNORE_GROUPS_LABEL'
                      )
                    }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{
                      $t(
                        'AGENT_BOTS.FORM.FOCOSIA.IGNORED_CONTACTS.IGNORE_GROUPS_HELP_TEXT'
                      )
                    }}
                  </span>
                </div>
                <Switch
                  v-model="formState.focosiaIgnoreGroups"
                  :aria-label="
                    $t(
                      'AGENT_BOTS.FORM.FOCOSIA.IGNORED_CONTACTS.IGNORE_GROUPS_LABEL'
                    )
                  "
                />
              </div>
              <div class="flex flex-col gap-2">
                <span class="text-sm font-medium text-n-slate-12">
                  {{
                    $t(
                      'AGENT_BOTS.FORM.FOCOSIA.IGNORED_CONTACTS.IDENTIFIERS_LABEL'
                    )
                  }}
                </span>
                <TagInput
                  v-model="formState.focosiaIgnoredTargets"
                  allow-create
                  :placeholder="
                    $t(
                      'AGENT_BOTS.FORM.FOCOSIA.IGNORED_CONTACTS.IDENTIFIERS_PLACEHOLDER'
                    )
                  "
                />
                <p class="text-xs text-n-slate-11">
                  {{
                    $t(
                      'AGENT_BOTS.FORM.FOCOSIA.IGNORED_CONTACTS.IDENTIFIERS_HELP_TEXT'
                    )
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div v-if="showAccessTokenInput" class="flex flex-col gap-1">
          <label
            v-if="type === MODAL_TYPES.EDIT"
            class="mb-0.5 text-sm font-medium text-n-slate-12"
          >
            {{ $t('AGENT_BOTS.ACCESS_TOKEN.TITLE') }}
          </label>
          <AccessToken
            v-if="type === MODAL_TYPES.EDIT"
            :value="accessToken"
            @on-copy="onCopyToken"
            @on-reset="onResetToken"
          />
          <AccessToken
            v-else
            :value="accessToken"
            :show-reset-button="false"
            @on-copy="onCopyToken"
          />
        </div>
      </div>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('AGENT_BOTS.FORM.CANCEL')"
          @click="onClickClose()"
        />
        <NextButton
          v-if="!showAccessToken"
          type="submit"
          data-testid="label-submit"
          :label="confirmButtonLabel"
          :is-loading="isLoading"
          :disabled="v$.$invalid"
        />
      </div>
    </form>
  </Dialog>
</template>
